<template>
    <el-card class="displaysettings absolute top-right fs-3 zi-100">
        <div class="flex-level pb-3 bb-1">
            <h3 class="flex m-0">{{ $l10n.__('Gestion & ordre des colonnes') }}</h3>
            <a
                class="close-btn"
                @click.prevent="$store.commit(`${listinghash}/hideDisplay`, 'columns')"
            >
                &times;
            </a>
        </div>

        <p class="my-3 gray-60">
            {{
                $l10n.__(
                    "Modifiez l'ordre des labels visibles pour organiser votre tableau comme vous le désirez.",
                )
            }}
            {{
                $l10n.__(
                    'Gérez les informations visibles dans votre tableau en cochant les colonnes que vous souhaitez afficher.',
                )
            }}
        </p>

        <div class="flex-level flex-top columns-sortable bb-1">
            <!-- LEFT COLUMN -->
            <div class="flex pr-3 hp-10">
                <h4 class="mt-0">{{ $l10n.__('Colonnes visibles') }}</h4>

                <div class="column-list-container ovy-auto">
                    <div v-if="mainColumn" class="p-1 pl-3 flex-level">
                        <div class="flex">
                            {{ mainColumn.label }}
                        </div>
                    </div>
                    <draggable
                        v-model="activeColumns"
                        :options="{ group: 'columns', draggable: '.itm-column' }"
                    >
                        <template v-for="column in activeColumns">
                            <div
                                v-if="!column.isMain"
                                :key="`displayed_${column.name}`"
                                class="p-1 pl-3 flex-level"
                                :class="{ 'itm-column cursor-move': !column.isMain }"
                            >
                                <div class="flex">
                                    <badge
                                        v-if="column.name.startsWith('cf_')"
                                        dot
                                        inline
                                        tooltip
                                        :badges="getBadges(column)"
                                    >
                                        {{ column.label }}
                                    </badge>
                                    <span v-else>{{ column.label }}</span>
                                </div>

                                <a
                                    href="javascript:;"
                                    class="close-btn"
                                    @click="removeDisplay(column)"
                                >
                                    &times;
                                </a>
                            </div>
                        </template>
                    </draggable>
                </div>
            </div>

            <div class="flex pl-3 bl-1 hp-10">
                <h4 class="mt-0">{{ $l10n.__('Colonnes disponibles') }}</h4>

                <!-- COLUMN SEARCHBAR -->
                <div class="form-group my-2 prelative">
                    <div class="form-input wp-10">
                        <input
                            type="text"
                            class="h-4 wp-10 search_input"
                            ref="searchcolumn"
                            v-model="searchword"
                            :placeholder="$t.__('common.actions.search_column')"
                        />
                        <a
                            href="javascript:;"
                            @click="searchword = ''"
                            v-if="searchword != ''"
                            class="close-btn search_clear"
                            >&times;</a
                        >
                    </div>
                </div>

                <!-- LIST -->
                <ul class="columns_list">
                    <li
                        class="p-1 pl-0"
                        v-for="column in filteredColumns"
                        :key="`available_${column.name}`"
                    >
                        <label :for="column.name" class="flex-row">
                            <input
                                type="checkbox"
                                class="wauto"
                                :checked="column.display"
                                :disabled="column.isMain"
                                :id="column.name"
                                @change="toggleDisplay($event, column)"
                            />
                            <badge
                                v-if="column.name.startsWith('cf_')"
                                dot
                                inline
                                tooltip
                                :badges="getBadges(column)"
                            >
                                {{ column.label }}
                            </badge>
                            <span v-else>{{ column.label }}</span>
                        </label>
                    </li>
                </ul>
            </div>
        </div>

        <div class="displaysettings__footer">
            <CompassPlainButton
                :label="$t.__('common.actions.reset_columns')"
                variant="neutral"
                leftIcon="revert"
                @click.prevent="resetDisplay"
            />
            <CompassButton :label="$t.__('common.actions.apply')" @click="apply" />
        </div>
    </el-card>
</template>

<script>
import { Card } from 'element-ui';
import draggable from 'vuedraggable';
import { removeAccents } from 'Core/commons/Utilities';
import Badge from 'vcomponents/utilities/Badge.vue';
import TagHumansConfig from 'Config/authenticated/TagHumans';

export default {
    props: {
        listinghash: {
            type: String,
            required: true,
        },
    },

    components: {
        draggable,
        Badge,
    },

    data() {
        return {
            canApply: true,
            columns: [],
            originalDisplay: [],
            searchword: '',
        };
    },

    created() {
        this.initData();
    },

    computed: {
        activeColumns: {
            get() {
                return this.columns
                    .filter((col) => col.display)
                    .sort((a, b) => {
                        if (a.rank < b.rank) {
                            return -1;
                        } else if (a.rank > b.rank) {
                            return 1;
                        }
                        return 0;
                    });
            },
            set(columns) {
                this.updateColumnDisplay(columns);
            },
        },

        filteredColumns() {
            if (this.searchword.trim() != '') {
                var regexp = new RegExp(removeAccents(this.searchword.trim()), 'i');

                return this.columns.filter((col) => {
                    return regexp.test(removeAccents(col.label));
                });
            }

            return this.columns;
        },

        mainColumn() {
            let mainIndex = _.findIndex(this.columns, { isMain: true });
            if (mainIndex != -1) {
                return this.columns[mainIndex];
            }

            return false;
        },
    },

    methods: {
        toggleDisplay(evt, column) {
            let display = evt.target.checked;
            let index = _.findIndex(this.columns, column);

            if (index != -1) {
                Vue.set(this.columns[index], 'display', display);
            }
        },

        removeDisplay(column) {
            let index = _.findIndex(this.columns, column);
            if (index != -1) {
                Vue.set(this.columns[index], 'display', false);
            }
        },

        updateColumnDisplay(columns, display = true) {
            let mainIndex = _.findIndex(this.columns, { isMain: true });

            // Force main column at rank 0
            if (mainIndex != -1) {
                let mainCol = {
                    ...this.columns[mainIndex],
                };
                mainCol.display = true;
                mainCol.rank = 0;

                Vue.set(this.columns, mainIndex, mainCol);
            }

            // Take care of other columns
            columns.forEach((col, rank) => {
                if (!col.isMain) {
                    let index = _.findIndex(this.columns, { name: col.name });

                    if (index != -1) {
                        col.rank = rank + 1;
                        Vue.set(this.columns, index, col);
                        rank++;
                    }
                }
            });
        },

        apply() {
            // if no new columns > no need to fetch, just call server for columns update

            let needFetch = false;

            let activeKeys = this.activeColumns.map((col) => col.name);

            if (_.isEqual(activeKeys, this.originalDisplay)) {
                return;
            }

            for (let col of this.activeColumns) {
                let index = this.originalDisplay.indexOf(col.name);
                let wasAlreadyThere = index != -1;

                if (!wasAlreadyThere) {
                    needFetch = true;
                }
            }

            this.$store.commit(`${this.listinghash}/updateColumns`, this.columns);

            if (needFetch) {
                this.$store.dispatch(`${this.listinghash}/fetch`).then((response) => {
                    this.initData();
                });
            } else {
                this.$store
                    .dispatch(`${this.listinghash}/updateColumns`)
                    .then((response) => this.initData());
            }

            this.$store.commit(`${this.listinghash}/hideDisplay`, 'columns');
        },

        initData() {
            this.columns = JSON.parse(JSON.stringify(this.$store.state[this.listinghash].columns));

            this.originalDisplay = this.columns
                .filter((col) => col.display)
                .sort((a, b) => {
                    if (a.rank < b.rank) {
                        return -1;
                    } else if (a.rank > b.rank) {
                        return 1;
                    }
                    return 0;
                })
                .map((col) => col.name);
        },

        resetDisplay() {
            this.$store
                .dispatch(`${this.listinghash}/resetColumns`)
                .then((response) => {
                    this.columns = JSON.parse(
                        JSON.stringify(this.$store.state[this.listinghash].columns),
                    );
                })
                .catch((e) => {
                    null;
                });
        },

        /**
         * TODO: see if possible to refacto methode => also exist (FiltersPane.vue)
         * Filter on TagHumansConfig only tagHuman who match with relationTypes that use the custom field
         *
         * @param {{name: String}} {more} - prop of column, particulary "more" of column who contain "useOn[]"
         * @return {Object[]} Return an array of tagHumans
         */
        getBadges({ more }) {
            const relationTypes = more ? more.useOn : [];

            return Object.entries(TagHumansConfig)
                .map(([id, tag]) => ({ id, ...tag })) // instruction can be remove if we add directly id for each tag human
                .filter(({ id }) => relationTypes.includes(id));
        },
    },
};
</script>

<style lang="sass">

@import '~@sellsy/styleguide/src/_variables';
@import '~@sellsy/styleguide/src/_mixins';

.displaysettings {
    width       : 520px;
    z-index     : 100;
    transition  : 300ms;

    &__footer {
        align-items: center;
        display: flex;
        gap: var(--spacing-2);
        justify-content: flex-end;
        padding: var(--spacing-3) 0 0;
    }
}

.column-list-container {
    height : 280px;

    &.with-searchbar {
        height : 228px;
    }
}


.itm-column{
    position : relative;
    .close-btn{
        display:none;
    }

    @include border-radius();

    &:hover{
        background-color : $primary-05;

        &:before{
            font-family: 'icomoon' !important;
            content: "\e96c";
            // content     :"⋮⋮";
            position    :absolute;
            top         :4px;
            left        :2px;
            cursor      :pointer;
            color       : $gray-60;
        }

        .close-btn{
            display:block;
        }
    }

    &.sortable-drag{
        background  : $white;
        border      : 1px solid $border-color;

        @include border-radius;
        @include box-shadow;
    }

    &.sortable-ghost{
        background-color    : $lightgrey;
        text-indent         : -1000;
    }
}


.columns_list{
    height: calc(280px - 36px - 8px);
    overflow-y: auto;
    margin:0px;
}


.search_input{
    padding-right:26px !important;
}

.search_clear{
    position: absolute;
    top: 10px;
    right: 8px;
}
</style>
